import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Loader2, ArrowLeft, RefreshCw, Save, Send, Hotel, ChevronDown, ChevronUp,
  CheckCircle2, XCircle, User, CalendarRange, Shuffle, Unlink,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const confidenceBadge = (confidence, score) => {
  const map = {
    alta: "bg-emerald-100 text-emerald-800 hover:bg-emerald-100",
    media: "bg-amber-100 text-amber-800 hover:bg-amber-100",
    baja: "bg-red-100 text-red-700 hover:bg-red-100",
    manual: "bg-violet-100 text-violet-800 hover:bg-violet-100",
  };
  const label = confidence === "manual"
    ? "asignado a mano"
    : `match ${confidence} · ${(score * 100).toFixed(0)}%`;
  return (
    <Badge className={`rounded-md ${map[confidence] || "bg-muted"}`}>{label}</Badge>
  );
};

const statusBadge = (status) => {
  const s = (status || "").toLowerCase();
  if (s.includes("confirmed"))
    return <Badge className="rounded-md bg-emerald-100 text-emerald-800 hover:bg-emerald-100">{status}</Badge>;
  if (s.includes("required"))
    return <Badge className="rounded-md bg-amber-100 text-amber-800 hover:bg-amber-100">{status}</Badge>;
  return status ? <Badge variant="secondary" className="rounded-md">{status}</Badge> : null;
};

const F = ({ id, label, value, onChange, type = "text", hint }) => (
  <div className="space-y-1">
    <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</Label>
    <Input
      data-testid={`match-field-${id}`}
      type={type}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      className="rounded-md h-8 text-sm"
    />
    {hint && <p className="text-[10px] text-muted-foreground/70">{hint}</p>}
  </div>
);

function matchKey(m) {
  return m.kimkim_item.addon_id || m.kimkim_item.new_booking_url || m.sofi_reservation.rid;
}

function kkKey(it) {
  return it.addon_id || it.new_booking_url || "";
}

const UNLINK_VALUE = "__unlink__";

/**
 * Lista plana de todos los items kimkim del viaje: los emparejados y los libres.
 * Útil para los selectores de re-emparejado.
 */
function buildKkOptions(trip, excludeKkKey = "") {
  if (!trip) return [];
  const opts = [];
  (trip.matches || []).forEach((m) => {
    const k = m.kimkim_item;
    const key = kkKey(k);
    if (!key || key === excludeKkKey) return;
    opts.push({
      key,
      title: k.title || "(sin título)",
      day: k.day || "",
      assigned_to: m.sofi_reservation?.service || "",
      assigned_rid: m.sofi_reservation?.rid || "",
    });
  });
  (trip.unmatched_kimkim || []).forEach((k) => {
    const key = kkKey(k);
    if (!key || key === excludeKkKey) return;
    opts.push({
      key,
      title: k.title || "(sin título)",
      day: k.day || "",
      assigned_to: "",
      assigned_rid: "",
    });
  });
  return opts;
}

function RematchSelect({ trip, rid, currentKkKey, onRematch, disabled, testIdPrefix = "rematch-select" }) {
  const options = useMemo(() => buildKkOptions(trip, currentKkKey), [trip, currentKkKey]);
  return (
    <Select
      value=""
      onValueChange={(v) => {
        if (!v) return;
        onRematch(rid, v === UNLINK_VALUE ? "" : v);
      }}
      disabled={disabled}
    >
      <SelectTrigger
        data-testid={`${testIdPrefix}-${rid}`}
        className="rounded-md h-8 text-xs"
      >
        <SelectValue placeholder={currentKkKey ? "Cambiar item kimkim…" : "Emparejar con kimkim…"} />
      </SelectTrigger>
      <SelectContent>
        {currentKkKey && (
          <SelectItem value={UNLINK_VALUE} data-testid={`${testIdPrefix}-${rid}-unlink`}>
            <span className="flex items-center gap-2 text-red-700">
              <Unlink className="h-3 w-3" /> Romper emparejamiento
            </span>
          </SelectItem>
        )}
        {options.length === 0 && (
          <div className="text-xs text-muted-foreground p-2">No hay items kimkim disponibles</div>
        )}
        {options.map((o) => (
          <SelectItem key={o.key} value={o.key} data-testid={`${testIdPrefix}-${rid}-opt-${o.key}`}>
            <div className="flex flex-col">
              <span className="text-sm">
                {o.day && <span className="text-muted-foreground/70 mr-2">{o.day}</span>}
                {o.title}
              </span>
              {o.assigned_to && (
                <span className="text-[10px] text-amber-700 flex items-center gap-1">
                  <Shuffle className="h-2.5 w-2.5" /> swap con: {o.assigned_to.slice(0, 60)}
                </span>
              )}
            </div>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function MatchCard({ row, onChange, trip, onRematch, rematching }) {
  const [open, setOpen] = useState(false);
  const r = row.sofi_reservation;
  const k = row.kimkim_item;
  const setField = (key) => (val) => onChange({ fields: { ...row.fields, [key]: val } });
  const key = matchKey(row);
  const pushed = row.push_result?.status;
  const isHotel = !!row.is_hotel;

  return (
    <div
      className={`rounded-md border ${pushed === "success" ? "border-emerald-300 bg-emerald-50/40" : pushed === "error" ? "border-red-300 bg-red-50/40" : "border-border bg-card"}`}
      data-testid={`match-row-${key}`}
    >
      <div className="flex items-center gap-3 p-3">
        <Checkbox
          data-testid={`match-checkbox-${key}`}
          checked={!!row.checked}
          disabled={!row.pushable}
          onCheckedChange={(v) => onChange({ checked: !!v })}
        />
        <div className="flex-1 min-w-0 grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] items-center gap-2">
          <div className="min-w-0">
            <p className="text-sm font-semibold truncate">{r.service}</p>
            <p className="text-xs text-muted-foreground truncate">
              SOFI #{r.rid} · {r.start_date} {r.meeting_time || ""}
            </p>
          </div>
          <span className="text-primary text-sm hidden md:inline">→</span>
          <div className="min-w-0">
            <p className="text-sm font-semibold truncate flex items-center gap-1">
              {isHotel && <Hotel className="h-3.5 w-3.5 text-violet-600 shrink-0" />}
              <span className="truncate">{k.title}</span>
            </p>
            <p className="text-xs text-muted-foreground truncate">
              kimkim · {k.day}{!row.pushable && (k.new_booking_url ? "" : " · ya confirmado o reservado por kimkim")}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {pushed ? (
            pushed === "success" ? (
              <span className="flex items-center gap-1 text-emerald-700 text-xs font-semibold">
                <CheckCircle2 className="h-4 w-4" /> {row.push_result.kimkim_status_after || "Enviado"}
              </span>
            ) : (
              <span className="flex items-center gap-1 text-red-600 text-xs font-semibold" title={row.push_result.error}>
                <XCircle className="h-4 w-4" /> Error
              </span>
            )
          ) : (
            <>
              {confidenceBadge(row.confidence, row.score)}
              {statusBadge(k.status)}
            </>
          )}
          <button
            type="button"
            data-testid={`match-expand-${key}`}
            onClick={() => setOpen(!open)}
            className="p-1 rounded hover:bg-muted"
          >
            {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="border-t border-border p-3 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid={`match-details-${key}`}>
          <F id="confirmation_number" label="Confirmation #" value={row.fields.confirmation_number} onChange={setField("confirmation_number")} />
          <F id="meeting_time" label="Hora inicio" type="time" value={row.fields.meeting_time} onChange={setField("meeting_time")} />
          <F id="end_time" label="Hora fin" type="time" value={row.fields.end_time} onChange={setField("end_time")} />
          <F id="operator_name" label="Operador (no se envía)" value={row.fields.operator_name} onChange={setField("operator_name")} hint="Queda en blanco a propósito" />
          <F id="departure_location" label="Salida" value={row.fields.departure_location} onChange={setField("departure_location")} />
          <F id="arrival_location" label="Llegada" value={row.fields.arrival_location} onChange={setField("arrival_location")} />
          <div className="col-span-2 space-y-1">
            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">
              Notas para el viajero ({r.template ? `template #${r.template.template_id}` : "campo Notas SOFI"})
              <span className="ml-1 text-muted-foreground/60 normal-case">— HTML enriquecido (párrafos, negrita, listas, enlaces) que kimkim renderiza en su editor</span>
            </Label>
            <Textarea
              data-testid={`match-field-notes-${key}`}
              rows={4}
              value={row.fields.notes || ""}
              onChange={(e) => setField("notes")(e.target.value)}
              className="rounded-md text-xs font-mono"
            />
          </div>
          <div className="col-span-2 md:col-span-4 space-y-1 border-t border-border pt-3 mt-1">
            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1">
              <Shuffle className="h-3 w-3" /> Cambiar emparejamiento manualmente
            </Label>
            <RematchSelect
              trip={trip}
              rid={r.rid}
              currentKkKey={k.addon_id || k.new_booking_url}
              onRematch={onRematch}
              disabled={rematching === r.rid}
              testIdPrefix="match-rematch"
            />
            <p className="text-[10px] text-muted-foreground/70">
              Selecciona el item correcto: si ya tiene otra reserva asignada se hará swap automático; si está libre, este match toma el nuevo item y el antiguo vuelve a "Sin emparejar".
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default function TripDetail({ tripId, onBack }) {
  const [trip, setTrip] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [reanalyzing, setReanalyzing] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [rematching, setRematching] = useState(null);

  const hydrate = useCallback((doc) => {
    setTrip(doc);
    setRows((doc.matches || []).map((m) => ({
      ...m,
      checked: m.pushable && m.push_result?.status !== "success",
    })));
    setDirty(false);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/trips/${tripId}`);
      hydrate(data);
    } catch {
      toast.error("No se pudo cargar el viaje");
    } finally {
      setLoading(false);
    }
  }, [tripId, hydrate]);

  useEffect(() => { load(); }, [load]);

  const handleRematch = useCallback(async (rid, new_kk_key) => {
    if (dirty && !window.confirm("Tienes cambios sin guardar. ¿Aplicar el cambio de emparejamiento perderá lo no guardado. ¿Continuar?")) return;
    setRematching(rid);
    try {
      const { data } = await axios.post(`${API}/trips/${tripId}/rematch`, { rid, new_kk_key });
      hydrate(data);
      if (!new_kk_key) {
        toast.success("Emparejamiento roto. La reserva pasa a 'Sin emparejar'.");
      } else {
        toast.success("Emparejamiento actualizado");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo cambiar el emparejamiento");
    } finally {
      setRematching(null);
    }
  }, [tripId, hydrate, dirty]);

  // Polling automático cuando el viaje está en estado processing o el push corriendo
  useEffect(() => {
    if (!trip) return;
    const polling = trip.status === "processing" || trip.push_job?.status === "running";
    if (!polling) return;
    const id = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API}/trips/${tripId}`);
        hydrate(data);
        if (trip.status === "processing" && data.status === "ready") {
          toast.success(`Viaje listo · ${data.matches.length} emparejamientos`);
        } else if (trip.status === "processing" && data.status === "error") {
          toast.error(data.status_message || "Error procesando el viaje");
        }
        if (trip.push_job?.status === "running" && data.push_job?.status === "done") {
          const { success, errors } = data.push_job;
          if (errors === 0) toast.success(`${success} confirmaciones enviadas a kimkim`);
          else toast.warning(`${success} enviadas, ${errors} con error`);
        }
      } catch {
        // ignorar errores transitorios durante el polling
      }
    }, 2000);
    return () => clearInterval(id);
  }, [trip, tripId, hydrate]);

  const updateRow = (key, patch) => {
    setRows((rs) => rs.map((r) => (matchKey(r) === key ? { ...r, ...patch } : r)));
    if (patch.fields) setDirty(true);
  };

  const saveEdits = useCallback(async () => {
    setSaving(true);
    try {
      const payload = {
        matches: rows.map((r) => ({
          addon_id: r.kimkim_item.addon_id,
          new_booking_url: r.kimkim_item.new_booking_url,
          fields: r.fields,
        })),
      };
      const { data } = await axios.put(`${API}/trips/${tripId}`, payload);
      hydrate(data);
      toast.success("Cambios guardados");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error guardando");
    } finally {
      setSaving(false);
    }
  }, [rows, tripId, hydrate]);

  const reanalyze = useCallback(async () => {
    if (dirty && !window.confirm("Tienes cambios sin guardar. ¿Re-analizar perderá lo no guardado. ¿Continuar?")) return;
    setReanalyzing(true);
    try {
      const { data } = await axios.post(`${API}/trips/${tripId}/reanalyze`, {}, { timeout: 180000 });
      hydrate(data);
      toast.success(`Re-analizado · ${data.matches.length} emparejamientos`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error re-analizando");
    } finally {
      setReanalyzing(false);
    }
  }, [tripId, dirty, hydrate]);

  const checkedRows = useMemo(() => rows.filter((r) => r.checked && r.pushable), [rows]);
  const isProcessing = trip?.status === "processing";
  const isError = trip?.status === "error";
  const pushJob = trip?.push_job;
  const isPushing = pushJob?.status === "running";
  const pushProgressPct = isPushing && pushJob.total > 0 ? Math.round((pushJob.processed / pushJob.total) * 100) : 0;

  const pushSelected = useCallback(async () => {
    if (checkedRows.length === 0) return;
    if (dirty && !window.confirm("Tienes cambios sin guardar. ¿Guardar y enviar de todos modos?")) return;
    setPushing(true);
    try {
      if (dirty) {
        await axios.put(`${API}/trips/${tripId}`, {
          matches: rows.map((r) => ({
            addon_id: r.kimkim_item.addon_id,
            new_booking_url: r.kimkim_item.new_booking_url,
            fields: r.fields,
          })),
        });
      }
      const selected_keys = checkedRows.map((r) => r.kimkim_item.addon_id || r.kimkim_item.new_booking_url);
      // El POST devuelve inmediatamente con push_job en running; el polling
      // del effect anterior actualiza el progreso en pantalla.
      const { data } = await axios.post(`${API}/trips/${tripId}/push`, { selected_keys }, { timeout: 30000 });
      hydrate(data);
      toast.success(`Enviando ${data.push_job.total} confirmaciones a kimkim…`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error lanzando el envío");
    } finally {
      setPushing(false);
    }
  }, [checkedRows, dirty, rows, tripId, hydrate]);

  if (loading || !trip) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="trip-detail">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <Button data-testid="trip-back-button" variant="ghost" size="sm" onClick={onBack} className="rounded-md">
          <ArrowLeft className="h-4 w-4 mr-1" /> Viajes guardados
        </Button>
        <div className="flex items-center gap-2 ml-auto">
          <Button
            data-testid="trip-reanalyze-button"
            variant="outline" size="sm"
            onClick={reanalyze}
            disabled={reanalyzing || pushing || isProcessing || isPushing}
            className="rounded-md"
          >
            {reanalyzing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />}
            Re-analizar
          </Button>
          <Button
            data-testid="trip-save-button"
            variant="outline" size="sm"
            onClick={saveEdits}
            disabled={saving || !dirty || isProcessing || isPushing}
            className={`rounded-md ${dirty ? "border-amber-400 text-amber-700" : ""}`}
          >
            {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
            {dirty ? "Guardar cambios" : "Guardado"}
          </Button>
          <Button
            data-testid="trip-push-button"
            onClick={pushSelected}
            disabled={pushing || checkedRows.length === 0 || isProcessing || isPushing}
            className="rounded-md font-semibold"
          >
            {(pushing || isPushing) ? (
              <span className="flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Enviando…</span>
            ) : (
              <span className="flex items-center gap-2"><Send className="h-4 w-4" /> Confirmar seleccionados ({checkedRows.length})</span>
            )}
          </Button>
        </div>
      </div>

      {isPushing && (
        <div data-testid="push-progress-banner" className="rounded-md border border-blue-300 bg-blue-50/60 p-4 space-y-2">
          <div className="flex items-center gap-3">
            <Loader2 className="h-5 w-5 animate-spin text-blue-700 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm text-blue-900">
                Enviando confirmaciones a kimkim
                <span className="ml-2 font-mono text-blue-700">
                  {pushJob.processed} / {pushJob.total}
                </span>
                {pushJob.errors > 0 && (
                  <span className="ml-2 text-red-600 text-xs font-semibold">
                    ({pushJob.errors} con error)
                  </span>
                )}
              </p>
              {pushJob.current_item && (
                <p className="text-xs text-blue-800 truncate" data-testid="push-current-item">
                  Procesando ahora: <span className="font-mono">{pushJob.current_item}</span>
                </p>
              )}
            </div>
            <span className="text-2xl font-bold text-blue-900 tabular-nums" data-testid="push-progress-pct">
              {pushProgressPct}%
            </span>
          </div>
          <div className="h-2 w-full bg-blue-200 rounded-full overflow-hidden">
            <div
              data-testid="push-progress-bar"
              className="h-full bg-blue-600 transition-all duration-300 ease-out"
              style={{ width: `${pushProgressPct}%` }}
            />
          </div>
        </div>
      )}

      {isProcessing && (
        <div data-testid="trip-processing-banner" className="rounded-md border border-amber-300 bg-amber-50/60 p-4 flex items-center gap-3">
          <Loader2 className="h-5 w-5 animate-spin text-amber-700 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm text-amber-900">Importando datos…</p>
            <p className="text-xs text-amber-800">
              {trip.status_message || "Leyendo todas las reservas en SOFI y la agenda en kimkim. Esto puede tardar 30–90 segundos según el tamaño del viaje."}
            </p>
          </div>
        </div>
      )}

      {isError && (
        <div data-testid="trip-error-banner" className="rounded-md border border-red-300 bg-red-50/60 p-4 flex items-center gap-3">
          <XCircle className="h-5 w-5 text-red-700 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm text-red-900">No se pudo importar el viaje</p>
            <p className="text-xs text-red-800 break-words">{trip.status_message || "Error desconocido"}</p>
            <p className="text-xs text-red-700 mt-1">Pulsa "Re-analizar" para volver a intentarlo.</p>
          </div>
        </div>
      )}

      <Card className="shadow-none border-border" data-testid="trip-detail-card">
        <CardHeader className="pb-3 border-b border-border">
          <div className="flex flex-wrap justify-between gap-2">
            <div>
              <h3 className="font-heading text-lg font-bold tracking-tight flex items-center gap-2">
                <User className="h-5 w-5 text-primary" />
                {trip.client_name || "(sin cliente)"}
              </h3>
              <p className="text-xs text-muted-foreground flex items-center gap-2 mt-1">
                <CalendarRange className="h-3 w-3" />
                {trip.sofi_header?.start_date} → {trip.sofi_header?.end_date}
                <span className="text-muted-foreground/60">·</span>
                {trip.sofi_header?.destination || ""}
                <span className="text-muted-foreground/60">·</span>
                SOFI <a className="hover:underline" target="_blank" rel="noreferrer" href={trip.sofi_header?.source_url}>#{trip.sofi_trip_id}</a>
                <span className="text-muted-foreground/60">·</span>
                kimkim <a className="hover:underline" target="_blank" rel="noreferrer" href={trip.kimkim_trip_url}>{trip.kimkim_trip_code}</a>
              </p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-3 space-y-2">
          {isProcessing && rows.length === 0 && (
            <div className="py-8 flex flex-col items-center gap-3 text-muted-foreground" data-testid="trip-processing-empty">
              <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
              <p className="text-sm">Aún no hay emparejamientos. La página se actualizará automáticamente cuando termine.</p>
            </div>
          )}
          {rows.map((row) => (
            <MatchCard
              key={matchKey(row)}
              row={row}
              onChange={(patch) => updateRow(matchKey(row), patch)}
              trip={trip}
              onRematch={handleRematch}
              rematching={rematching}
            />
          ))}

          {(trip.unmatched_sofi?.length > 0 || trip.unmatched_kimkim?.length > 0) && (
            <div className="mt-4 rounded-md border border-dashed border-border p-3 space-y-3" data-testid="unmatched-section">
              <p className="text-xs uppercase tracking-wider text-muted-foreground">Sin emparejar</p>

              {trip.unmatched_sofi?.length > 0 && (
                <div className="space-y-2">
                  <p className="text-[11px] font-semibold text-muted-foreground/80">Reservas SOFI sin item kimkim</p>
                  {trip.unmatched_sofi.map((res) => (
                    <div
                      key={`uns-${res.rid}`}
                      data-testid={`unmatched-sofi-${res.rid}`}
                      className="grid grid-cols-1 md:grid-cols-[1fr_320px] gap-2 items-center p-2 rounded bg-muted/30"
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate flex items-center gap-1">
                          {res.is_hotel && <Hotel className="h-3.5 w-3.5 text-violet-600 shrink-0" />}
                          {res.service}
                        </p>
                        <p className="text-[11px] text-muted-foreground">
                          SOFI #{res.rid} · {res.start_date}{res.meeting_time && ` ${res.meeting_time}`}
                        </p>
                      </div>
                      <RematchSelect
                        trip={trip}
                        rid={res.rid}
                        currentKkKey=""
                        onRematch={handleRematch}
                        disabled={rematching === res.rid}
                        testIdPrefix="unmatched-rematch"
                      />
                    </div>
                  ))}
                </div>
              )}

              {trip.unmatched_kimkim?.length > 0 && (
                <div className="space-y-1">
                  <p className="text-[11px] font-semibold text-muted-foreground/80">Items kimkim sin reserva SOFI</p>
                  {trip.unmatched_kimkim.map((it, i) => (
                    <p key={(it.addon_id || it.new_booking_url || i) + "-kk"} className="text-xs text-muted-foreground pl-2">
                      · {it.title} ({it.day || "—"})
                      {!it.new_booking_url && <span className="ml-1 text-muted-foreground/60">[reservado por kimkim]</span>}
                    </p>
                  ))}
                </div>
              )}
            </div>
          )}

          {trip.kimkim_sourced?.length > 0 && (
            <div
              className="mt-4 rounded-md border border-violet-200 bg-violet-50/40 p-3 space-y-2"
              data-testid="kimkim-sourced-section"
            >
              <p className="text-xs uppercase tracking-wider text-violet-700 font-semibold flex items-center gap-1">
                <Hotel className="h-3.5 w-3.5" />
                Hoteles reservados desde KimKim — omitidos
              </p>
              <p className="text-[11px] text-violet-800/80">
                Estos hoteles tienen a "KimKim" como proveedor en SOFI: la información ya viene directamente de la plataforma kimkim, así que no se emparejan ni se envían.
              </p>
              {trip.kimkim_sourced.map((res) => (
                <div
                  key={`kks-${res.rid}`}
                  data-testid={`kimkim-sourced-${res.rid}`}
                  className="flex items-center gap-2 p-2 rounded bg-white/60 border border-violet-100"
                >
                  <Hotel className="h-3.5 w-3.5 text-violet-600 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{res.service}</p>
                    <p className="text-[11px] text-violet-800/70">
                      SOFI #{res.rid} · {res.start_date}
                      {res.end_date && ` → ${res.end_date}`}
                      {res.operator_name && ` · proveedor: ${res.operator_name}`}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
